import { TradeIcon } from '@components/TradeIcon/TradeIcon';
import { TRADE_ICON_FILTER } from '@components/TradeIcon/tradeIconFilter';
import { PROFESSION_FALLBACK_COLOR, professionChipStyle, professionColor } from '@utils/professionColor';
import styles from './TradePill.module.css';

interface ITradePillProps {
  /** Text shown inside the pill (e.g. profession / specialty / trade group). */
  label: string;
  /**
   * Fill colour. Sources: upstream profession's `backgroundColor`, or a
   * caller-derived hash fallback. Optional — pill renders with a neutral
   * default when omitted.
   */
  backgroundColor?: string;
  /**
   * `tradeId` used to fetch the per-trade pictogram from the backend
   * storage proxy. Optional — text-only when omitted (e.g. managers
   * have no trade icon).
   */
  tradeId?: string;
  /** Pictogram size (default 14, matching Monitor's `.tradeChip`). A number is
   *  px; pass a CSS length like `'1em'` to size it relative to the pill font so
   *  it scales with the scheduler zoom (see ProfessionPill's compact variant). */
  iconSize?: number | string;
  /**
   * `md` (default) is the canonical Monitor/Tags chip. `sm` is the compact
   * variant for dense rows (the scheduler's ~28px label column): smaller
   * type, tighter padding, and it yields to the cell width instead of
   * claiming its own 220px cap.
   */
  size?: 'md' | 'sm';
}

/**
 * Coloured profession pill — a rounded fill containing an optional
 * `<TradeIcon>` SVG and a text label. Shared across MonitorPage
 * WorkforceTab, IssuesPage ProfessionsFilter, and the MultiSelect's
 * collapsible-group chip so trade pills look identical everywhere.
 *
 * Mirrors trusstor's `ProfessionDisplayWithTradeIcon`. Background colour
 * comes from the consumer (the profession record's `backgroundColor`), or
 * the neutral light-gray fallback when absent. Foreground (text + icon) is
 * paired with the fill via `professionChipStyle` — white on a real colour,
 * dark on the fallback — so the pill stays legible either way.
 */
export function TradePill({ label, backgroundColor, tradeId, iconSize = 14, size = 'md' }: ITradePillProps) {
  const fill = professionColor(backgroundColor);
  const isFallback = fill === PROFESSION_FALLBACK_COLOR;
  return (
    <span
      className={size === 'sm' ? `${styles.pill} ${styles.pillSm}` : styles.pill}
      style={professionChipStyle(backgroundColor)}
    >
      {tradeId && (
        <TradeIcon
          tradeId={tradeId}
          size={iconSize}
          filter={isFallback ? TRADE_ICON_FILTER.dark : TRADE_ICON_FILTER.white}
        />
      )}
      <span className={styles.label} title={label}>{label}</span>
    </span>
  );
}
